// @ts-check
// Cabeçalho da conversa: avatar, nome, telefone ou e-mail, chips da pessoa, da família e dos cards
// abertos (com a etapa), seletor de responsável e "Vincular a uma pessoa" pelo seletor de registros.
import { html } from 'htm/preact';
import { useRef, useState } from 'preact/hooks';
import { Avatar } from '../../ui/avatar.js';
import { Chip } from '../../ui/chip.js';
import { Icon } from '../../ui/icon.js';
import { Button, LightIconButton } from '../../ui/button.js';
import { Dropdown } from '../../ui/dropdown.js';
import { RecordPickerList } from '../object-record/record-picker.js';
import { AssigneePicker } from './conversation-assignee.js';
import { ChannelIcon, StageTag, formatPersonName, getContactHandleLabel, getThreadDisplayName } from './conversation-display.js';
import { t } from '../../i18n/index.js';

/** @typedef {import('../../data/conversations.js').ConversationThread} ConversationThread */

/**
 * @param {{ thread: ConversationThread, onLinkPerson: (personId: string | null) => void }} props
 */
function LinkPersonControl({ thread, onLinkPerson }) {
  const anchorRef = useRef(/** @type {HTMLSpanElement | null} */ (null));
  const [isOpen, setIsOpen] = useState(false);
  const hasPerson = Boolean(thread.person);
  const personLabel = formatPersonName(thread.person?.name);
  return html`<span ref=${anchorRef} class="conversation-link-person">
    ${hasPerson
      ? html`<${LightIconButton} icon="IconLink" ariaLabel=${t('conversations.header.changePerson')} onClick=${() => setIsOpen(!isOpen)} />`
      : html`<${Button} title=${t('conversations.header.linkPerson')} icon="IconUserPlus" variant="secondary" size="small" onClick=${() => setIsOpen(!isOpen)} />`}
    <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${() => setIsOpen(false)} placement="bottom-end" width=${240}>
      <${RecordPickerList}
        objectNameSingular="person"
        selectedIds=${thread.person ? [thread.person.id] : []}
        preselectedRecords=${thread.person ? [{ id: thread.person.id, label: personLabel }] : []}
        allowEmpty=${hasPerson}
        onSelect=${(picked) => {
          setIsOpen(false);
          if ((picked?.id ?? null) !== (thread.person?.id ?? null)) onLinkPerson(picked?.id ?? null);
        }}
      />
    <//>
  </span>`;
}

/**
 * @param {{ thread: ConversationThread, onLinkPerson: (personId: string | null) => void, onAssign: (workspaceMemberId: string | null) => void,
 *   onBack?: () => void }} props
 */
export function ConversationHeader({ thread, onLinkPerson, onAssign, onBack }) {
  const displayName = getThreadDisplayName(thread);
  const handleLabel = getContactHandleLabel(thread);
  const personName = formatPersonName(thread.person?.name);
  const hasLinks = Boolean(thread.person) || thread.households.length > 0 || thread.enrollmentOpportunities.length > 0;
  return html`<header class="conversation-header">
    ${onBack ? html`<${LightIconButton} icon="IconChevronLeft" size="medium" ariaLabel=${t('conversations.back')} className="conversation-back" onClick=${onBack} />` : null}
    <span class="conversation-header-avatar">
      <${Avatar} placeholder=${displayName} seed=${thread.person?.id ?? thread.contact?.handle} size="xl" type="rounded" avatarUrl=${thread.person?.avatarUrl ?? null} />
    </span>
    <div class="conversation-header-main">
      <div class="conversation-header-title-row">
        <h2 class="conversation-header-name">${thread.channelType === 'EMAIL' && thread.subject ? thread.subject : displayName}</h2>
      </div>
      <div class="conversation-header-handle">
        <${ChannelIcon} channelType=${thread.channelType} size=${14} />
        ${thread.channelType === 'EMAIL' && thread.subject ? html`<span class="conversation-header-contact-name">${displayName}</span>` : null}
        <span>${handleLabel}</span>
        ${thread.contact?.displayName && personName && thread.contact.displayName !== personName && thread.channelType === 'WHATSAPP'
          ? html`<span class="conversation-header-profile">~${thread.contact.displayName}</span>`
          : null}
      </div>
      ${hasLinks
        ? html`<div class="conversation-header-links">
            ${thread.person
              ? html`<${Chip}
                  label=${personName}
                  variant="highlighted"
                  href=${`/object/person/${thread.person.id}`}
                  title=${t('conversations.header.openPerson')}
                  leftComponent=${html`<${Avatar} placeholder=${personName} seed=${thread.person.id} size="sm" type="rounded" avatarUrl=${thread.person.avatarUrl ?? null} />`}
                />`
              : null}
            ${thread.households.map((household) => html`<${Chip}
              key=${household.id}
              label=${household.name}
              variant="highlighted"
              href=${`/object/household/${household.id}`}
              title=${`${t('conversations.header.family')}: ${household.name}`}
              leftComponent=${html`<${Icon} name="IconHome" size=${14} />`}
            />`)}
            ${thread.enrollmentOpportunities.map((opportunity) => html`<span key=${opportunity.id} class="conversation-header-opportunity">
              <${Chip}
                label=${opportunity.name}
                variant="highlighted"
                href=${`/object/enrollmentOpportunity/${opportunity.id}`}
                title=${`${t('conversations.header.opportunity')}: ${opportunity.name}`}
                leftComponent=${html`<${Icon} name="IconTargetArrow" size=${14} />`}
              />
              <${StageTag} stage=${opportunity.stage} />
            </span>`)}
          </div>`
        : null}
    </div>
    <div class="conversation-header-actions">
      <${AssigneePicker} threadId=${thread.id} assignee=${thread.assignee} onAssign=${onAssign} />
      <${LinkPersonControl} thread=${thread} onLinkPerson=${onLinkPerson} />
    </div>
  </header>`;
}
